import {
    call,
    delay,
    put,
    race,
    select,
    take,
    takeLatest
} from 'redux-saga/effects';
import get from 'lodash.get';
import fileDownload from 'js-file-download';
import moment from 'moment';

// Types
import type { Saga } from 'redux-saga';

// Constants
import {
    REPORTS_HARVEST_TIMESHEETS_ALL_LOAD_REQUESTED,
    REPORTS_HARVEST_TIMESHEETS_ALL_POLLING_REQUESTED,
    REPORTS_HARVEST_TIMESHEETS_ALL_POLLING_CANCELED,
    REPORTS_HARVEST_TIMESHEETS_ALL_EXPORT_REQUESTED
} from './constants';

// Actions
import setHash from '../hashes';
import { enqueueSnackbar } from '../notifications/actions';
import {
    succeededLoadReportsHarvestTimesheetsAll,
    failedLoadReportsHarvestTimesheetsAll,
    succeededPollingReportsHarvestTimesheetsAll,
    failedPollingReportsHarvestTimesheetsAll,
    succeededExportReportsHarvestTimesheetsAll,
    failedExportReportsHarvestTimesheetsAll,
    setReportsHarvestTimesheetsAll
} from './actions';

// Selectors
import { selectCurrentOrganizationId } from '../currentOrganizationId/selectors';
import { selectCurrentSeasonId } from '../currentSeasonId/selectors';
import { selectCurrentEntityId } from '../currentEntityId/selectors';
import { makeSelectHash } from '../hashes/selectors';
import { selectIsOnline } from '../connection/selectors';

// Utils
import axiosRetry from '../../utils/axiosRetry';

// Environment
const REACT_APP_API_URL: string = String(process.env.REACT_APP_API_URL);

// Constants
const namespace = 'reportsHarvestTimesheetsAll';

type Action = {
    +type: string,
    +payload: Object
};

type Config = ?{
    headers?: Object,
    params?: Object
};

export function* getReportsHarvestTimesheetsAll(config: Config): Saga<void> {
    const organizationId = yield select(selectCurrentOrganizationId);
    const seasonId = yield select(selectCurrentSeasonId);
    const entityId = yield select(selectCurrentEntityId);

    const url = `${REACT_APP_API_URL}/v1/Reports/harvestTimesheets/all`;

    return yield call(axiosRetry, {
        method: 'GET',
        url,
        ...(config || {}),
        params: {
            ...(config && config.params ? config.params : {}),
            organizationId,
            filter: {
                require: {
                    $and: [
                        {
                            'event.entity.seasonId': seasonId,
                            // TODO Filter out archived entities from reporting
                            // 'event.entity.archivedAt': { $exists: false },
                            ...(entityId && {
                                $or: [
                                    {
                                        'event.entity.path': {
                                            $like: `/${entityId}/%`
                                        }
                                    },
                                    {
                                        'event.entityId': entityId
                                    }
                                ]
                            })
                        }
                    ]
                }
            }
        }
    });
}

export function* loadReportsHarvestTimesheetsAll(action: Action): Saga<void> {
    try {
        const response = yield call(getReportsHarvestTimesheetsAll);

        const hash = get(response, 'headers.x-hash');

        // Succeeded Load Reports Harvest Inspections
        yield put(succeededLoadReportsHarvestTimesheetsAll(response));

        // Set Reports Harvest Inspections
        yield put(setReportsHarvestTimesheetsAll(response.data.data));

        // Set Hash
        yield put(setHash(namespace, hash));
    } catch (error) {
        yield put(failedLoadReportsHarvestTimesheetsAll(error));
    }
}

export function* watchReportsHarvestTimesheetsAllLoadRequested(): Saga<void> {
    yield takeLatest(
        REPORTS_HARVEST_TIMESHEETS_ALL_LOAD_REQUESTED,
        loadReportsHarvestTimesheetsAll
    );
}

export function* pollReportsHarvestTimesheetsAll(action: Action): Saga<void> {
    while (true) {
        yield delay(60000);

        // Select Is Online
        const isOnline = yield select(selectIsOnline);

        // Select Hash
        const hash = yield select(makeSelectHash(namespace));
        let hashMatch = false;

        if (isOnline) {
            try {
                if (hash) {
                    const hashResponse = yield call(
                        getReportsHarvestTimesheetsAll,
                        {
                            params: { hash: true }
                        }
                    );

                    hashMatch = hash === get(hashResponse, 'headers.x-hash');
                }
                if (!hashMatch) {
                    const response = yield call(getReportsHarvestTimesheetsAll);

                    const nextHash = get(response, 'headers.x-hash');

                    // Succeeded Polling Reports Harvest Inspections
                    yield put(
                        succeededPollingReportsHarvestTimesheetsAll(response)
                    );

                    // Set Reports Harvest Inspections
                    yield put(
                        setReportsHarvestTimesheetsAll(response.data.data)
                    );

                    // Set Hash
                    yield put(setHash(namespace, nextHash));
                }
            } catch (error) {
                // Failed Polling Reports Harvest Inspections
                yield put(failedPollingReportsHarvestTimesheetsAll(error));
            }
        }
    }
}

export function* watchReportsHarvestTimesheetsAllPollingRequested(): Saga<void> {
    while (true) {
        const action = yield take(
            REPORTS_HARVEST_TIMESHEETS_ALL_POLLING_REQUESTED
        );
        yield race([
            call(pollReportsHarvestTimesheetsAll, action),
            take(REPORTS_HARVEST_TIMESHEETS_ALL_POLLING_CANCELED)
        ]);
    }
}

export function* exportReportsHarvestTimesheetsAll(): Saga<void> {
    try {
        const response = yield call(getReportsHarvestTimesheetsAll, {
            headers: {
                'Content-Type': 'text/csv; charset=utf-8',
                Accept: 'text/csv'
            }
        });

        const time = moment.utc().format();

        // File Download
        fileDownload(response.data, `reports_harvest_timesheets_${time}.csv`);

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Export successful',
                options: { variant: 'success' }
            })
        );

        // Succeeded Export Reports Harvest Inspections
        yield put(succeededExportReportsHarvestTimesheetsAll(response));
    } catch (error) {
        const message = get(
            error,
            'response.data.message',
            'There was an error. Please try again later.'
        );

        // Enqueue Snackbar
        yield put(enqueueSnackbar({ message, options: { variant: 'error' } }));

        // Failed Export Reports Harvest Inspections
        yield put(failedExportReportsHarvestTimesheetsAll(error));
    }
}

export function* watchReportsHarvestTimesheetsAllExportRequested(): Saga<void> {
    yield takeLatest(
        REPORTS_HARVEST_TIMESHEETS_ALL_EXPORT_REQUESTED,
        exportReportsHarvestTimesheetsAll
    );
}
